// Khối "AI đọc cả mùa" ở bước Handoff (260928 E3-P3 K9).
//
// AI đọc tập 1..N đã DRAFT như một khán giả (lô 5 tập + sổ nhớ) và chỉ CHẶN handoff chế độ theo đoạn nguồn khi thấy lỗi liên tập khách quan
// có bằng chứng trích nguyên văn. KHÔNG tự sửa: người dùng chọn "Viết lại tập X" / "Viết lại tuần tự từ tập X" / "Vẫn tiếp tục" / "AI đọc lại".
// Mọi quyết định hiển thị nằm ở `seasonReaderView.ts` (hàm thuần, có jest) — file này chỉ vẽ lại.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Collapse, Input, Modal, Popconfirm, Space, Tag, Typography, message } from 'antd';
import Button from './workbench/WbButton';
import { adaptationApi } from '../../services/api/adaptation';
import type { SeasonReaderViewResponse } from '../../types/adaptation';
import { describeAdaptationError } from './adaptationUtils';
import { GateColor, isOverrideReasonValid, OVERRIDE_REASON_MIN } from './gateView';
import { ReaderIssueView, seasonReaderCardView, seasonReaderRunConfirmText } from './seasonReaderView';
import { useFilmJobPoll } from './useFilmJobPoll';
import { useAdoptedJobId } from './workbench/useAdoptedJobId';
import OptionsButton from './workbench/assistant/OptionsButton';
import { EPISODE_UNKNOWN_REASON, seasonIssueOptionsRequest } from './workbench/assistant/optionsAction';
import type { AssistantRequest } from './workbench/assistant/assistantIntent';

const { Text } = Typography;
const { TextArea } = Input;

interface Props {
  storyId: string;
  /** Viết lại tập (route `POST /adaptation/scripts`) — nơi gọi lo job + tiến độ. */
  onWriteScripts: (opts?: { from?: number; to?: number; force?: boolean }) => Promise<void>;
  /** Đang có việc nền khác (viết kịch bản…) ⇒ khoá nút. */
  disabled?: boolean;
  /** F36 — job đọc cả mùa BE báo đang chạy: nhận nuôi để có toast xong + tải lại sau tải lại trang / đổi chặng. */
  adoptJobId?: string | null;
  /** 261005 E1-P2: trạng thái + hàm tải lại do `useSeasonReader` (ở chặng ⑤) giữ — card không tự tải nữa. */
  resp: SeasonReaderViewResponse | null;
  reload: () => Promise<void>;
  /** Override/đọc xong ⇒ nơi gọi tải lại tiến trình. */
  onChanged: () => void;
  /** 261005 E3-P4b2 — "Phương án (tốn AI)" ở mỗi vấn đề; nơi gọi lo đóng Drawer + mở khung Trợ lý đúng tập. Không truyền ⇒ không nút. */
  onAskOptions?: (req: AssistantRequest) => void;
  /** Lý do tắt nút theo tập đích (chặng khoá / tập không sửa được / đang sửa tay) — `ScriptStage` tính từ dữ liệu tập. */
  optionsDisabledFor?: (episode: number) => string | undefined;
}

const BORDER: Record<GateColor, string> = { default: 'var(--wb-btn-line)', success: 'var(--wb-ok)', error: 'var(--wb-bad)', warning: 'var(--wb-warn)', info: 'var(--wb-text-2)' };
const TAG_COLOR: Record<GateColor, string> = { default: 'default', success: 'success', error: 'error', warning: 'warning', info: 'processing' };

const IssueRow: React.FC<{
  issue: ReaderIssueView; muted?: boolean; warning?: string; locked?: boolean;
  onRewrite?: (opts: { from: number; to: number; force: true }) => void;
  onAskOptions?: (req: AssistantRequest) => void;
  optionsDisabledFor?: (episode: number) => string | undefined;
}> = ({ issue, muted, warning, locked, onRewrite, onAskOptions, optionsDisabledFor }) => (
  <div style={{ marginBottom: 12 }}>
    <Space size={6} wrap>
      <Tag color={muted ? 'default' : 'red'} style={{ margin: 0 }}>{issue.dimensionLabel}</Tag>
      <Text strong>{issue.where}</Text>
      <Text>{issue.problem}</Text>
    </Space>
    {issue.quotes.map((q, i) => (
      <div key={i} style={{ marginTop: 4, paddingLeft: 10, borderLeft: `3px solid ${q.valid ? 'var(--wb-btn-line)' : 'var(--wb-warn)'}` }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{q.episode ? `Tập ${q.episode}: ` : ''}</Text>
        <Text type="secondary" italic style={{ fontSize: 13 }}>“{q.text}”</Text>
        {!q.valid && <Text type="warning" style={{ fontSize: 11 }}> (không khớp nguyên văn)</Text>}
      </div>
    ))}
    {issue.suggestedFix && <div style={{ marginTop: 2 }}><Text type="secondary" style={{ fontSize: 13 }}>💡 {issue.suggestedFix}</Text></div>}
    {onAskOptions && (() => {
      const req = seasonIssueOptionsRequest(issue);
      return (
        <Space size={4} wrap style={{ marginTop: 6 }}>
          <OptionsButton disabledReason={req ? optionsDisabledFor?.(issue.episode) : EPISODE_UNKNOWN_REASON} onClick={req ? () => onAskOptions(req) : undefined} />
        </Space>
      );
    })()}
    {onRewrite && (issue.rewriteOne || issue.rewriteFrom) && (
      <Space size={6} wrap style={{ marginTop: 6 }}>
        {[issue.rewriteOne, issue.rewriteFrom].map((rw) => rw && (
          <Popconfirm
            key={rw.label} title={rw.label} description={<div style={{ maxWidth: 360 }}>{warning}</div>}
            okText="Viết lại" cancelText="Thôi" disabled={locked}
            onConfirm={() => onRewrite({ from: rw.from, to: rw.to, force: true })}
          >
            <Button size="small" disabled={locked}>{rw.label}</Button>
          </Popconfirm>
        ))}
      </Space>
    )}
  </div>
);

const SeasonReaderCard: React.FC<Props> = ({ storyId, onWriteScripts, disabled, adoptJobId, resp, reload, onChanged, onAskOptions, optionsDisabledFor }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const { jobId, setJobId, release: releaseJob } = useAdoptedJobId(storyId, adoptJobId ?? null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState('');


  const view = useMemo(() => seasonReaderCardView(resp), [resp]);

  useFilmJobPoll(jobId, {
    onTerminal: async (outcome, task) => {
      releaseJob();
      if (outcome === 'completed') message.success('AI đã đọc xong cả mùa.');
      else if (outcome === 'failed') message.error(task?.detail?.error || 'AI đọc cả mùa thất bại — xem log.', 8);
      else message.warning('Đã dừng theo dõi — bấm Tải lại.');
      await reload();
      onChanged();
    },
  });

  if (!view) return null;
  const locked = !!disabled || !!busy || !!jobId || view.state === 'RUNNING';

  const fail = (e: unknown) => {
    const d = describeAdaptationError(e);
    message.error(`${d.title}${d.lines[0] ? ` — ${d.lines[0]}` : ''}`, 8);
  };

  const runReader = async () => {
    setBusy('run');
    try {
      const r = await adaptationApi.runSeasonReader(storyId);
      if (r?.jobId) setJobId(r.jobId);
      message.info('AI đang đọc cả mùa (nền)', 5);
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const override = async () => {
    if (!isOverrideReasonValid(reason)) return;
    setBusy('override');
    try {
      await adaptationApi.overrideSeasonReader(storyId, reason.trim());
      message.success('Đã vượt cổng — lý do được ghi vào kiểm toán.', 5);
      setOverrideOpen(false);
      setReason('');
      await reload();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const rewrite = async (opts: { from: number; to: number; force: true }) => {
    setBusy('rewrite');
    try { await onWriteScripts(opts); } finally { setBusy(null); }
  };

  return (
    <>
      <Card
        size="small" type="inner" style={{ marginBottom: 12, borderColor: BORDER[view.color] }}
        title={<Space size={8} wrap><Text strong>AI đọc cả mùa</Text><Tag color={TAG_COLOR[view.color]} style={{ margin: 0 }}>{view.label}</Tag>{view.blocksHandoff && <Tag color="red" style={{ margin: 0 }}>chặn handoff</Tag>}</Space>}
        extra={resp?.gate?.model ? <Text type="secondary" style={{ fontSize: 12 }}>{resp.gate.model}</Text> : undefined}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>{view.description}</Text>
          {view.rangeText && <Text type="secondary" style={{ fontSize: 13 }}>{view.rangeText}</Text>}

          {view.blocking.map((i) => (
            <IssueRow key={i.id} issue={i} warning={view.rewriteWarning} locked={locked} onRewrite={rewrite} onAskOptions={onAskOptions} optionsDisabledFor={optionsDisabledFor} />
          ))}

          {view.advisory.length > 0 && (
            <Collapse
              size="small" ghost
              items={[{
                key: 'advisory',
                label: <Text type="secondary" style={{ fontSize: 13 }}>Gợi ý tham khảo ({view.advisory.length}) — không chặn</Text>,
                children: view.advisory.map((i) => <IssueRow key={i.id} issue={i} muted onAskOptions={onAskOptions} optionsDisabledFor={optionsDisabledFor} />),
              }]}
            />
          )}

          {view.overrideInfo && <Alert type="info" showIcon message={`Vượt cổng bởi ${view.overrideInfo}`} />}
          {view.notes.map((n) => <Text key={n} type="secondary" style={{ fontSize: 13 }}>ℹ {n}</Text>)}
          {view.state === 'RUNNING' && <Alert type="info" showIcon message="AI đang đọc cả mùa ở nền" description="Các nút bị khoá tới khi xong; trạng thái tự cập nhật." />}

          <Space wrap>
            {view.canRun && (
              <Popconfirm
                title={`${view.runLabel}?`}
                description={seasonReaderRunConfirmText(resp?.draftMax ?? 0)}
                okText="Chạy" cancelText="Thôi" disabled={locked} onConfirm={runReader}
              >
                <Button type="primary" loading={busy === 'run' || !!jobId} disabled={locked}>{view.runLabel}</Button>
              </Popconfirm>
            )}
            {view.canOverride && (
              <Button disabled={locked} onClick={() => { setReason(''); setOverrideOpen(true); }}>Vẫn tiếp tục</Button>
            )}
            <Button size="small" type="link" disabled={!!busy} onClick={() => { reload(); }}>Tải lại trạng thái</Button>
          </Space>
          <Text type="secondary" style={{ fontSize: 13 }}>{view.chainNote}</Text>
        </Space>
      </Card>

      <Modal
        open={overrideOpen} title="Vượt cổng AI đọc cả mùa — có kiểm toán" okText="Xác nhận vượt cổng" cancelText="Huỷ"
        okButtonProps={{ disabled: !isOverrideReasonValid(reason), danger: true, loading: busy === 'override' }}
        onOk={override} onCancel={() => setOverrideOpen(false)}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>
            Bạn đang bỏ qua <Text strong>{view.blocking.length} lỗi liên tập khách quan</Text> để đẩy kịch bản sang Chapters. Người thực hiện,
            thời gian, lý do và danh sách lỗi bỏ qua sẽ được ghi vào truyện. Việc này KHÔNG vượt cổng "Nối tập".
          </Text>
          <Text strong>Lý do vượt cổng (bắt buộc, tối thiểu {OVERRIDE_REASON_MIN} ký tự)</Text>
          <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="vd: đã đọc tay, mâu thuẫn này là chủ ý của tác giả" maxLength={500} />
        </Space>
      </Modal>
    </>
  );
};

export default SeasonReaderCard;
